import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SearchBar from "./SearchBar";
import { deleteScam, dislikeScam, incCommentCount, likeScam, neutralScam } from "src/redux/scam/scamActions";
import { createComment, getCommentsByScamId, updateReaction } from "src/api_endpoints/userDashboard/ScamAPI";
import ViewScamImagesModal from "./viewScamImagesModal";
import ScamAlertCommonCard from "./ScamAlertCommonCard";
import ViewCommentsModal from "./ViewCommentsModal";
import ConfirmationModal from "./ConfirmationModal";

const MyPostedScams = () => {
  const scams = useSelector((state) => state.scamState?.myScamAlertPost) || [];
  const { id: userId, first_name, last_name, user_avatar } = useSelector((state) => state.userState?.userData);
  const dispatch = useDispatch();
  const [filteredScams, setFilteredScams] = useState([]);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [openCommentsModal, setOpenCommentsModal] = useState(false);
  const [currentScamComments, setCurrentScamComments] = useState([]);
  const [deleteScamId, setDeleteScamId] = useState(null)

  let [scamId, setScamId] = useState(null);
  let [scamCode, setScamCode] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleDeleteScam = async () => {
    dispatch(deleteScam({ scam_id: deleteScamId, user_id: userId }))
    setShowConfirmationModal(false);
  };
  const cancelConfirm = () => {
    setShowConfirmationModal(false);
  };

  const handleOpenCommentsModal = async (scm) => {
    try {
      const scam_id = scm?.scamPK;
      const scam_code=scm?.scam_code
      if (!scam_id) {
        throw new Error("Invalid scam ID provided");
      }

      const res = await getCommentsByScamId({ scam_id ,user_id:userId});
      setScamId(scam_id)
      setScamCode(scam_code)
      if (res) {
        setCurrentScamComments(res.comments);
        setOpenCommentsModal(true);
      } else {
        throw new Error("Failed to fetch comments or no comments found.");
      }
    } catch (error) {
      console.error("Error fetching comments:", error.message);
    }
  };

  const handleCreateComment = async (comment, scamId) => {
    try {
      const res = await createComment({
        user_id: userId,
        scam_id: scamId,
        comment
      });
      if (res) {
        dispatch(incCommentCount(scamId))
        setCurrentScamComments((prevComments) => [
          ...prevComments,
          {
            user_id: userId,
            comment: comment,
            status: 1, // Assuming the new comment is active by default
            date_created: new Date().toISOString(), // Current timestamp in ISO format
            first_name, // Replace with dynamic values
            last_name,
            user_avatar,
            id:res.commentId
          }
        ]);
      } else {
        throw new Error(res.message || "Failed to create comment");
      }
    } catch (error) {
      console.error("Error creating comment:", error.message);
    }
  };

  const handleCloseCommentsModal = () => {
    setOpenCommentsModal(false);
  };


  useEffect(() => {
    setFilteredScams(scams);
  }, [scams]);

  useEffect(() => {
    if (searchInput.trim() === "") {
      setFilteredScams(scams);
    } else {
      const filtered = scams.filter((scam) =>
        scam.scam_code.toString().includes(searchInput.trim())
      );
      setFilteredScams(filtered);
    }
  }, [searchInput, scams]);

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleReaction = async (scamId, newReaction) => {
    try {
      const formData = new FormData();
      formData.append("scam_code", scamId);
      formData.append("user_id", userId);
      formData.append("reaction_type", newReaction);
      await updateReaction(formData);

      if (newReaction === 1) {
        dispatch(likeScam(scamId));
      } else if (newReaction === 2) {
        dispatch(dislikeScam(scamId));
      } else {
        dispatch(neutralScam(scamId));
      }
    } catch (err) {
      console.error("Failed to update reaction:", err);
      setError("An error occurred while updating your reaction.");
    }
  };

  const handleOpenModal = (images) => {
    setCurrentImages(images);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleToggleLikeInParent = (index, scam_comment_id, isLikedComment, totalLikesOnReplyComment) => {
    // Clone current comments to update the specific comment
    const updatedComments = [...currentScamComments];
    
    // Toggle the like status and update the like count
    updatedComments[index].isLikedComment = !isLikedComment;
    updatedComments[index].totalLikesOnReplyComment = isLikedComment
      ? totalLikesOnReplyComment - 1
      : totalLikesOnReplyComment + 1;
    setCurrentScamComments(updatedComments);
  };
  

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ padding: "1rem", textAlign: "center" }}>
        {error}
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column-reverse", sm: "row" },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            padding: { xs: 0, sm: "16px" },
            margin: { xs: ".5rem", sm: "0" },
          }}
        >
          My reports
        </Typography>
        <SearchBar
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          handleSearchChange={handleSearchChange}
        />
      </Box>
      {filteredScams.length === 0 ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ padding: "1rem", textAlign: "center", marginTop: '2rem' }}
        >
          No scam post found.
        </Typography>
      ) : (
        filteredScams
          .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
          .map((scam, index) => (
            <ScamAlertCommonCard
              key={index}
              scam={scam}
              onReaction={handleReaction}
              onOpenModal={handleOpenModal}
              onCloseModal={handleCloseModal}
              openModal={openModal}
              isViewMyReports={true}
              handleOpenCommentsModal={handleOpenCommentsModal}
              setShowConfirmationModal={setShowConfirmationModal}
              handleDeleteScam={handleDeleteScam}
              setDeleteScamId={setDeleteScamId}
            />
          ))
      )}

      <ViewScamImagesModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        currentImages={currentImages}
      />
      <ViewCommentsModal
        commentData={currentScamComments}  // Pass current comments
        open={openCommentsModal}
        handleClose={handleCloseCommentsModal}
        scamId={scamId}
        scamCode={scamCode}
        handleCreateComment={handleCreateComment}
        handleToggleLikeInParent={handleToggleLikeInParent}
      />
      {showConfirmationModal && <ConfirmationModal text="Do you want to delete?" onConfirm={handleDeleteScam} onCancel={cancelConfirm} />}

    </>
  );
};

export default MyPostedScams;
